import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetchAllContracts } from '../../hooks/use-fetch-all-contracts';
import {
  BillingType,
  ClientType,
  PartnershipType,
  ScopeType,
  useGetAllClientsQuery,
  useGetAllInsurersQuery,
  useGetBillingTypesQuery,
  useGetClientInsurersByIdsLazyQuery,
  useGetPartnershipsQuery,
  useGetPaymentTypesQuery,
} from '../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../utils/bills-utils';
import { addAllOption, InsurerType } from './tool-validation-schema';

interface UseBillingToolDataParams {
  clientId: number | undefined;
  insurerId: number | undefined;
  partnershipId: number | undefined;
  startDate: Date;
  endDate: Date;
}

interface BillingTypeOption {
  value: string;
  displayName: string;
}

interface PaymentTypeOption {
  label: string;
  value: string;
}

export interface UseBillingToolDataReturn {
  clientOptions: ClientType[];
  insurerOptions: InsurerType[];
  partnershipOptions: PartnershipType[];
  billingTypeOptions: BillingTypeOption[];
  paymentTypeOptions: PaymentTypeOption[];
  isFilterLoading: boolean;
  isInitialLoading: boolean;
  hasError: boolean;
  hasData: boolean;
}

const sortByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.name.localeCompare(b.name));

const formatBillingTypes = (billingTypes: Set<string>): BillingTypeOption[] =>
  Array.from(billingTypes)
    .sort((a, b) => a.localeCompare(b))
    .map(bt => ({
      value: bt,
      displayName: capitalizeFirstLetterAndSpaceSnakeString(bt),
    }));

export const useBillingToolData = ({
  clientId,
  insurerId,
  partnershipId,
  startDate,
  endDate,
}: UseBillingToolDataParams): UseBillingToolDataReturn => {
  const {
    data: clientsData,
    error: clientsError,
    loading: clientsLoading,
  } = useGetAllClientsQuery();
  const {
    data: insurersData,
    error: insurersError,
    loading: insurersLoading,
  } = useGetAllInsurersQuery();
  const {
    data: partnershipsData,
    error: partnershipsError,
    loading: partnershipsLoading,
  } = useGetPartnershipsQuery({ variables: { type: 'billing' } });
  const {
    data: paymentTypeData,
    error: paymentTypeError,
    loading: paymentTypeLoading,
  } = useGetPaymentTypesQuery();
  const {
    data: billingTypesData,
    error: billingTypesError,
    loading: billingTypesLoading,
  } = useGetBillingTypesQuery();

  const [getClientInsurersByIds, { loading: clientInsurerLoading }] =
    useGetClientInsurersByIdsLazyQuery();
  const { fetchAllContracts } = useFetchAllContracts();
  const contractBillingMapRef = useRef(new Map<string, Set<string>>());

  const sortedClients = useMemo(
    () => sortByName(clientsData?.getAllClients ?? []),
    [clientsData],
  );
  const sortedInsurers = useMemo<InsurerType[]>(
    () => sortByName(insurersData?.getAllInsurers ?? []),
    [insurersData],
  );
  const sortedPartnerships = useMemo(
    () => sortByName(partnershipsData?.getPartnerships ?? []),
    [partnershipsData],
  );
  const allBillingTypes = useMemo(
    () =>
      [...(billingTypesData?.getBillingTypes ?? [])].sort((a, b) =>
        a.value.localeCompare(b.value),
      ),
    [billingTypesData],
  );

  const paymentTypeOptions = useMemo<PaymentTypeOption[]>(
    () =>
      (paymentTypeData?.getPaymentTypes ?? [])
        .map(pt => ({ label: pt.code, value: pt.code }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [paymentTypeData],
  );

  const [clientOptions, setClientOptions] = useState<ClientType[]>([]);
  const [insurerOptions, setInsurerOptions] = useState<InsurerType[]>([]);
  const [billingTypeOptions, setBillingTypeOptions] = useState<
    BillingTypeOption[]
  >([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  useEffect(() => {
    if (sortedClients.length > 0) {
      setClientOptions(addAllOption(sortedClients));
    }
  }, [sortedClients]);

  useEffect(() => {
    setInsurerOptions(sortedInsurers);
  }, [sortedInsurers]);

  useEffect(() => {
    setBillingTypeOptions(
      allBillingTypes.map((type: BillingType) => ({
        value: type.value,
        displayName: capitalizeFirstLetterAndSpaceSnakeString(type.value),
      })),
    );
  }, [allBillingTypes]);

  useEffect(() => {
    const isAllClientSet = clientId === -1;
    const clientIsSet = clientId && !isAllClientSet;

    if (partnershipId && !clientIsSet && !insurerId) {
      return;
    }

    if (clientIsSet || insurerId) {
      getClientInsurersByIds({
        variables: {
          clientIds: clientIsSet ? [clientId] : [],
          insurerIds: insurerId ? [insurerId] : [],
          scope: ScopeType.Exclusive,
        },
      }).then(({ data }) => {
        if (data?.getClientInsurersByIds) {
          const reduced = data.getClientInsurersByIds.reduce<{
            clientOpts: { [key: number]: ClientType };
            insurerOpts: { [key: number]: InsurerType };
          }>(
            (coll, { client, insurer }) => ({
              clientOpts: client
                ? { ...coll.clientOpts, [client.id]: client }
                : coll.clientOpts,
              insurerOpts: insurer
                ? {
                    ...coll.insurerOpts,
                    [insurer.id]: {
                      id: insurer.id,
                      identifier: insurer.identifier,
                      name: insurer.name,
                    },
                  }
                : coll.insurerOpts,
            }),
            { clientOpts: {}, insurerOpts: {} },
          );
          if (!clientId) {
            setClientOptions(
              addAllOption(sortByName(Object.values(reduced.clientOpts))),
            );
          }
          if (!insurerId) {
            setInsurerOptions(sortByName(Object.values(reduced.insurerOpts)));
          }
        }
      });
    } else {
      setClientOptions(addAllOption(sortedClients));
      setInsurerOptions(sortedInsurers);
      setBillingTypeOptions(
        allBillingTypes
          .map((type: BillingType) => ({
            value: type.value,
            displayName: capitalizeFirstLetterAndSpaceSnakeString(type.value),
          }))
          .sort((a, b) => a.value.localeCompare(b.value)),
      );
    }
  }, [
    clientId,
    insurerId,
    partnershipId,
    sortedClients,
    sortedInsurers,
    getClientInsurersByIds,
    allBillingTypes,
  ]);

  useEffect(() => {
    if (!partnershipId) {
      contractBillingMapRef.current = new Map();
      return;
    }

    const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
    const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');

    const baseVars = {
      clientIds: [] as number[],
      insurerIds: [] as number[],
      partnershipIds: [partnershipId],
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      includeVoided: false,
      size: 100,
    };

    setContractsLoading(true);
    Promise.all([
      fetchAllContracts({ ...baseVars, packageEnabled: true }),
      fetchAllContracts({ ...baseVars, packageEnabled: false }),
    ])
      .then(([packagedItems, nonPackagedItems]) => {
        const uniqueClientIds = new Set<number>();
        const uniqueInsurerIds = new Set<number>();
        const billingMap = new Map<string, Set<string>>();

        packagedItems.forEach(contract => {
          const key = `${contract.clientId}-${contract.insurerId}`;
          if (!billingMap.has(key)) {
            billingMap.set(key, new Set());
          }

          const types = billingMap.get(key) ?? new Set();

          if (contract.billingType) {
            types.add(contract.billingType);
          }

          contract.contractPackages?.forEach(cp => {
            if (cp?.billingTypeOverride) {
              types.add(cp.billingTypeOverride);
            }
          });

          if (contract.clientId) {
            uniqueClientIds.add(contract.clientId);
          }

          if (contract.insurerId) {
            uniqueInsurerIds.add(contract.insurerId);
          }
        });

        nonPackagedItems.forEach(contract => {
          const key = `${contract.clientId}-${contract.insurerId}`;

          if (!billingMap.has(key)) {
            billingMap.set(key, new Set());
          }

          const types = billingMap.get(key) ?? new Set();

          if (contract.billingType) {
            types.add(contract.billingType);
          }

          if (contract.clientId) {
            uniqueClientIds.add(contract.clientId);
          }

          if (contract.insurerId) {
            uniqueInsurerIds.add(contract.insurerId);
          }
        });

        contractBillingMapRef.current = billingMap;

        const filteredClients = sortedClients.filter(c =>
          uniqueClientIds.has(c.id),
        );
        const filteredInsurers = sortedInsurers.filter(i =>
          uniqueInsurerIds.has(i.id),
        );

        setClientOptions(addAllOption(filteredClients));
        setInsurerOptions(filteredInsurers);

        const allTypes = new Set<string>();
        billingMap.forEach(types => types.forEach(t => allTypes.add(t)));
        if (allTypes.size > 0) {
          setBillingTypeOptions(formatBillingTypes(allTypes));
        }
      })
      .finally(() => setContractsLoading(false));
  }, [
    partnershipId,
    fetchAllContracts,
    sortedClients,
    sortedInsurers,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (!partnershipId || !clientId) {
      return;
    }

    const map = contractBillingMapRef.current;

    if (map.size === 0) {
      return;
    }

    const filteredBillingTypes = new Set<string>();

    map.forEach((types, key) => {
      const [cId, iId] = key.split('-').map(Number);
      const clientMatch = clientId === -1 || cId === clientId;
      const insurerMatch = !insurerId || iId === insurerId;
      if (clientMatch && insurerMatch) {
        types.forEach(t => filteredBillingTypes.add(t));
      }
    });

    if (filteredBillingTypes.size > 0) {
      setBillingTypeOptions(formatBillingTypes(filteredBillingTypes));
    }
  }, [clientId, insurerId, partnershipId]);

  useEffect(() => {
    if (partnershipId || !clientId) {
      return;
    }

    const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
    const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');

    const baseVars = {
      clientIds: clientId !== -1 ? [clientId] : [],
      insurerIds: insurerId ? [insurerId] : [],
      partnershipIds: [] as number[],
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      includeVoided: false,
    };

    setContractsLoading(true);
    Promise.all([
      fetchAllContracts({ ...baseVars, packageEnabled: true, size: 100 }),
      fetchAllContracts({ ...baseVars, packageEnabled: false, size: 100 }),
    ])
      .then(([packagedItems, nonPackagedItems]) => {
        const uniqueBillingTypes = new Set<string>();

        packagedItems.forEach(({ billingType, contractPackages }) => {
          if (billingType) {
            uniqueBillingTypes.add(billingType);
          }

          contractPackages?.forEach(cp => {
            if (cp?.billingTypeOverride) {
              uniqueBillingTypes.add(cp.billingTypeOverride);
            }
          });
        });

        nonPackagedItems.forEach(({ billingType }) => {
          if (billingType) {
            uniqueBillingTypes.add(billingType);
          }
        });

        if (uniqueBillingTypes.size > 0) {
          setBillingTypeOptions(formatBillingTypes(uniqueBillingTypes));
        }
      })
      .finally(() => setContractsLoading(false));
  }, [
    clientId,
    insurerId,
    partnershipId,
    fetchAllContracts,
    startDate,
    endDate,
  ]);

  const isInitialLoading =
    clientsLoading ||
    insurersLoading ||
    partnershipsLoading ||
    paymentTypeLoading ||
    billingTypesLoading;

  const hasError = !!(
    clientsError ||
    insurersError ||
    partnershipsError ||
    paymentTypeError ||
    billingTypesError
  );

  const hasData =
    !!clientsData &&
    !!insurersData &&
    !!partnershipsData &&
    !!paymentTypeData &&
    !!billingTypesData;

  return {
    clientOptions,
    insurerOptions,
    partnershipOptions: sortedPartnerships,
    billingTypeOptions,
    paymentTypeOptions,
    isFilterLoading: clientInsurerLoading || contractsLoading,
    isInitialLoading,
    hasError,
    hasData,
  };
};
